<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import EmptyState from '@/components/EmptyState.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import PortfolioCompletenessPanel from '@/components/portfolio/PortfolioCompletenessPanel.vue'
import PortfolioImportPanel from '@/components/portfolio/PortfolioImportPanel.vue'
import PortfolioKpis from '@/components/portfolio/PortfolioKpis.vue'
import PortfolioTable from '@/components/portfolio/PortfolioTable.vue'
import type { ExtendedEnum } from '@/types'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import EstateObjectType = App.Enums.EstateObject.EstateObjectType

withDefaults(
  defineProps<{
    estateObjects: EstateObjectData[]
    estateObjectTypes: ExtendedEnum<EstateObjectType>[]
    missingFieldCounts: Record<string, number>
    activeFinancingRequestCounts: Record<string, number>
    portfolioCompletion: number
    hasPendingImport?: boolean
  }>(),
  {
    hasPendingImport: false,
  },
)

const { t } = useI18n()
</script>

<template>
  <Head :title="t('heading')" />

  <AppContextPanel>
    <div class="flex flex-col gap-6">
      <PortfolioCompletenessPanel
        :estate-objects="estateObjects"
        :missing-field-counts="missingFieldCounts"
        :completion="portfolioCompletion" />

      <PortfolioImportPanel :has-pending-import="hasPendingImport ?? false" :separated="!!estateObjects.length" />
    </div>
  </AppContextPanel>

  <div class="mb-6">
    <p class="section-label">{{ t('eyebrow') }}</p>
    <div class="flex items-center gap-2">
      <h1 class="page-title">{{ t('heading') }}</h1>
      <PageTitleHelp :text="t('subtitle')" />
    </div>
  </div>

  <template v-if="estateObjects.length">
    <PortfolioKpis :estate-objects="estateObjects" />

    <FinestCard :title="t('tableCard')" icon="pi pi-building" flush>
      <template #actions>
        <Link :href="EstateObjectsController.create()">
          <FinestButton variant="primary" size="md" :label="t('add')" icon="pi pi-plus" />
        </Link>
      </template>

      <PortfolioTable
        :estate-objects="estateObjects"
        :estate-object-types="estateObjectTypes"
        :missing-field-counts="missingFieldCounts"
        :active-financing-request-counts="activeFinancingRequestCounts" />
    </FinestCard>
  </template>

  <EmptyState v-else icon="pi pi-building" :title="t('emptyState.title')" :description="t('emptyState.description')">
    <template #action>
      <Link :href="EstateObjectsController.create()">
        <FinestButton variant="primary" size="md" :label="t('add')" icon="pi pi-plus" />
      </Link>
    </template>
  </EmptyState>
</template>

<i18n>
{
  "de": {
    "eyebrow": "Meine Basis",
    "heading": "Portfolio",
    "subtitle": "Dein gesamter Immobilienbestand auf einen Blick. Sortiere, filtere und gruppiere deine Objekte – ideal ab einem größeren Bestand.",
    "tableCard": "Deine Bestandsliste",
    "add": "Immobilie hinzufügen",
    "emptyState": {
      "title": "Keine Immobilien",
      "description": "Du hast noch keine Immobilien angelegt"
    }
  },
  "en": {
    "eyebrow": "My Base",
    "heading": "Portfolio",
    "subtitle": "Your entire property portfolio at a glance. Sort, filter and group your properties – ideal for larger holdings.",
    "tableCard": "Your property list",
    "add": "Add property",
    "emptyState": {
      "title": "No properties",
      "description": "You have not added any properties yet"
    }
  }
}
</i18n>
